import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Button,
  Alert,
  alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { macroAPI } from '../services/api';
import { ScoreSkeleton } from '../components/score';
import { getScoreColor, getScoreBg } from '../theme';

function Score() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [macroData, setMacroData] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await macroAPI.getCurrent();
      setMacroData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching macro scores:', error);
      setMessage({ type: 'error', text: 'Failed to load macro scores' });
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setCalculating(true);
      setMessage(null);
      await macroAPI.recalculate();
      setMessage({ type: 'success', text: 'Scores recalculated successfully!' });
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Error recalculating scores:', error);
      setMessage({ type: 'error', text: 'Failed to recalculate scores' });
    } finally {
      setCalculating(false);
    }
  };

  const getDirectionIcon = (direction) => {
    return direction === 1 ? (
      <TrendingUpIcon sx={{ fontSize: 16, color: '#11998e' }} />
    ) : (
      <TrendingDownIcon sx={{ fontSize: 16, color: '#ff6b6b' }} />
    );
  };

  if (loading) {
    return <ScoreSkeleton />;
  }

  if (!macroData) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Alert severity="warning">No macro score data available. Please recalculate.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('score.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('score.subtitle')}
          </Typography>
          {macroData && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {t('score.basedOn', { count: macroData.total_indicators || macroData.indicator_scores?.length || 33 })} • {t('score.updated')}: {new Date(macroData.calculation_date).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={calculating}
          >
            {t('score.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={calculating ? <CircularProgress size={20} /> : <CalculateIcon />}
            onClick={handleRecalculate}
            disabled={calculating}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
              },
            }}
          >
            {calculating ? t('score.calculating') : t('score.recalculate')}
          </Button>
        </Box>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Macro Summary Table - Additional 15 Indicators */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('score.macroSummary')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: alpha('#11998e', 0.1), borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">MOS ({t('score.opportunity')})</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#11998e' }}>
                  {parseFloat(macroData.mos || macroData.macro_score).toFixed(1)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: alpha('#ff6b6b', 0.1), borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">MRS ({t('score.risk')})</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff6b6b' }}>
                  {parseFloat(macroData.mrs || 0).toFixed(1)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: alpha('#667eea', 0.1), borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Net = MOS - MRS</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                  {parseFloat(macroData.net_score || macroData.macro_score).toFixed(1)}
                </Typography>
              </Box>
              <Box sx={{ 
                textAlign: 'center', 
                px: 3, 
                py: 1, 
                bgcolor: (() => {
                  const netScore = parseFloat(macroData.net_score || macroData.macro_score);
                  if (netScore >= 10) return alpha('#11998e', 0.15); // Green for Risk-on
                  if (netScore <= -10) return alpha('#ff6b6b', 0.15); // Red for Risk-off
                  return alpha('#ffa726', 0.15); // Orange/Yellow for Neutral
                })(),
                borderRadius: 1,
                border: '2px solid',
                borderColor: (() => {
                  const netScore = parseFloat(macroData.net_score || macroData.macro_score);
                  if (netScore >= 10) return '#11998e'; // Green
                  if (netScore <= -10) return '#ff6b6b'; // Red
                  return '#ffa726'; // Orange/Yellow
                })()
              }}>
                <Typography variant="caption" color="text.secondary">Regime</Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  color: (() => {
                    const netScore = parseFloat(macroData.net_score || macroData.macro_score);
                    if (netScore >= 10) return '#11998e'; // Green
                    if (netScore <= -10) return '#ff6b6b'; // Red
                    return '#ffa726'; // Orange/Yellow
                  })()
                }}>
                  {(() => {
                    const netScore = parseFloat(macroData.net_score || macroData.macro_score);
                    if (netScore >= 10) return t('market.regime.riskOn');
                    if (netScore <= -10) return t('market.regime.riskOff');
                    return t('market.regime.neutral');
                  })()}
                </Typography>
              </Box>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Indicator_ID</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Latest_Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Latest_Value</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Mean</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Stdev</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Z</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Good Direction</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Indicator Score (0-100)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Pillar</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Weight in Pillar</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Score x Weight</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Filter and display the 15 additional indicators */}
                {macroData.indicator_scores
                  .filter(ind => [
                    'FDI_DISB_YOY', 'DEPOSIT_12M_AVG', 'GVB_2Y', 'GVB_5Y', 'GVB_10Y', 
                    'YC_SLOPE_10Y2Y', 'DEPOSIT_GROWTH_YTD', 'OMO_NET_INJ_MTH', 'OMO_OUTSTANDING',
                    'CHN_PMI_NBS', 'CHN_PMI_CAIXIN', 'BRENT', 'OIL_BENEFIT', 'HRC_USD_TON', 'UREA_USD_MT'
                  ].includes(ind.indicator_id))
                  .map((indicator) => (
                    <TableRow key={indicator.indicator_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#667eea' }}>
                          {indicator.indicator_id}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {new Date(indicator.latest_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {parseFloat(indicator.latest_value).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {indicator.mean ? parseFloat(indicator.mean).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {indicator.stdev ? parseFloat(indicator.stdev).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            color: parseFloat(indicator.z_score) > 0 ? '#11998e' : '#ff6b6b'
                          }}
                        >
                          {parseFloat(indicator.z_score).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {getDirectionIcon(indicator.good_direction)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={parseFloat(indicator.indicator_score).toFixed(1)}
                          size="small"
                          sx={{
                            bgcolor: getScoreBg(indicator.indicator_score_level),
                            color: getScoreColor(indicator.indicator_score_level),
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={indicator.pillar_code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {indicator.weight_in_pillar ? parseFloat(indicator.weight_in_pillar).toFixed(2) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {indicator.indicator_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#764ba2' }}>
                          {parseFloat(indicator.score_x_weight).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pillar Scores Table - Matching Google Sheet Rows 22-29 */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('score.pillarScores')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#764ba2', 0.05) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Pillar</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Pillar Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Pillar Weight</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Pillar Score (0-100)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Final Contribution</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {macroData.pillar_scores.map((pillar) => {
                  const isRiskPillar = pillar.pillar_code === 'H';
                  return (
                    <TableRow key={pillar.pillar_code} hover sx={{ bgcolor: isRiskPillar ? alpha('#ff6b6b', 0.02) : 'transparent' }}>
                      <TableCell>
                        <Chip 
                          label={pillar.pillar_code} 
                          sx={{ 
                            bgcolor: isRiskPillar ? '#ff6b6b' : '#764ba2', 
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.9rem'
                          }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {pillar.pillar_name || pillar.pillar_code}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {pillar.pillar_weight ? (parseFloat(pillar.pillar_weight) * 100).toFixed(1) : '0.0'}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <Box
                            sx={{
                              width: 80,
                              height: 6,
                              bgcolor: alpha('#667eea', 0.1),
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${parseFloat(pillar.pillar_score || pillar.raw_score || 0)}%`,
                                height: '100%',
                                bgcolor: isRiskPillar ? '#ff6b6b' : getScoreColor(pillar.pillar_score_level),
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 45, color: isRiskPillar ? '#ff6b6b' : '#667eea' }}>
                            {parseFloat(pillar.pillar_score || pillar.raw_score || 0).toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isRiskPillar ? '#ff6b6b' : '#764ba2' }}>
                          {pillar.final_contribution ? parseFloat(pillar.final_contribution).toFixed(2) : '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={isRiskPillar ? t('score.risk') : t('score.opportunity')} 
                          size="small"
                          sx={{ 
                            bgcolor: isRiskPillar ? alpha('#ff6b6b', 0.1) : alpha('#11998e', 0.1),
                            color: isRiskPillar ? '#ff6b6b' : '#11998e',
                            fontWeight: 600,
                            border: '1px solid',
                            borderColor: isRiskPillar ? '#ff6b6b' : '#11998e'
                          }} 
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Indicator Scores Table - Matching Google Sheet Rows 3-20 */}
      <Card elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {t('score.indicatorScores')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha('#667eea', 0.05) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Indicator ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Latest Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Latest Value</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Mean</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Stdev</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Z-Score</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Good Dir</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Indicator Score</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Pillar</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Weight</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Score × Weight</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {macroData.indicator_scores.map((indicator) => (
                  <TableRow key={indicator.indicator_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#667eea' }}>
                        {indicator.indicator_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {indicator.indicator_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {new Date(indicator.latest_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {parseFloat(indicator.latest_value).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {indicator.mean ? parseFloat(indicator.mean).toFixed(2) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {indicator.stdev ? parseFloat(indicator.stdev).toFixed(2) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: parseFloat(indicator.z_score) > 0 ? '#11998e' : '#ff6b6b'
                        }}
                      >
                        {parseFloat(indicator.z_score).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {getDirectionIcon(indicator.good_direction)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={parseFloat(indicator.indicator_score).toFixed(1)}
                        size="small"
                        sx={{
                          bgcolor: getScoreBg(indicator.indicator_score_level),
                          color: getScoreColor(indicator.indicator_score_level),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={indicator.pillar_code} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {indicator.weight_in_pillar ? parseFloat(indicator.weight_in_pillar).toFixed(2) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {parseFloat(indicator.score_x_weight).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Score;

